import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  generateCumulativeTimeSeries,
  getColor,
  getPast12Months,
} from "../../utils/helpers";
import styles from "./GraphAccordion.module.scss";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import LineGraph from "../LineGraph/LineGraph";
import DownArrowIcon, {
  ReactComponent as DownArrow,
} from "../../icons/down-arrow.svg";
import UpArrowIcon, {
  ReactComponent as UpArrow,
} from "../../icons/up-arrow.svg";
import { ReactComponent as InfoCircle } from "../../icons/info-circle.svg";
import { ReactComponent as SortAscIcon } from "../../icons/sort-ascending.svg";
import { ArrowRight } from "lucide-react";
import classNames from "classnames";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";

const GraphAccordion = ({
  findingsData,
  auditsTasksData,
  improvementsData,
  meModeActive = false,
  currentUser,
  searchQuery = "",
  selectedRiskLevels = [],
  sortBy = "chapter",
}) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [openLetterGroups, setOpenLetterGroups] = useState([]);
  const [openChapterGroups, setOpenChapterGroups] = useState([]);
  const autoScrollRiskRef = useRef(null);
  const headerRowRef = useRef(null);
  const containerRef = useRef(null);

  const { risksData } = useSelector((state) => state.organization);

  console.log(risksData);

  const normalizedSearchQuery = String(searchQuery).trim().toLowerCase();

  const riskByRiskIdLower = useMemo(() => {
    const map = new Map();
    Object.values(risksData || {}).forEach((risk) => {
      const riskId = risk?.risk_id;
      if (!riskId) return;
      map.set(String(riskId).toLowerCase(), risk);
    });
    return map;
  }, [risksData]);

  const normalizedSelectedRiskLevels = useMemo(
    () =>
      new Set(
        (selectedRiskLevels || [])
          .map((level) => String(level).toLowerCase())
          .filter(Boolean),
      ),
    [selectedRiskLevels],
  );

  const qmsDescriptionById = useMemo(() => {
    const map = new Map();
    Object.values(risksData || {}).forEach((risk) => {
      const qmsId = risk?.qms_id;
      const qmsDescription = risk?.qms_description;
      if (!qmsId || !qmsDescription) return;
      if (!map.has(qmsId)) {
        map.set(qmsId, String(qmsDescription));
      }
    });
    return map;
  }, [risksData]);

  const chapterDescriptionByKey = useMemo(() => {
    const map = new Map();
    Object.values(risksData || {}).forEach((risk) => {
      const qmsId = risk?.qms_id;
      if (!qmsId) return;
      const chapterKey = String(qmsId).charAt(0).toUpperCase();
      if (!chapterKey) return;
      if (!map.has(chapterKey) && risk?.qms_description) {
        map.set(chapterKey, String(risk.qms_description));
      }
    });
    return map;
  }, [risksData]);

  // Get past 12 months for headers
  const months = getPast12Months();

  const [startYear, startMonth] = months[0].split("-");
  const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, 1);

  const [endYear, endMonth] = months[months.length - 1].split("-");
  const endDate = new Date(
    parseInt(endYear),
    parseInt(endMonth),
    0, // last day of the previous month (e.g., "2024-07" gives July 31)
  );

  // Helper function to check if a finding is relevant for the current user in me mode
  const isFindingRelevantForUser = (finding) => {
    if (!meModeActive) return true;

    return [
      finding.responsible,
      finding.accountable,
      finding.consulted,
      finding.informed,
      finding.reporter,
    ].some((role) =>
      Array.isArray(role) ? role.includes(currentUser) : role === currentUser,
    );
  };

  // Helper function to check if an improvement is relevant for the current user in me mode
  const isImprovementRelevantForUser = (improvement) => {
    if (!meModeActive) return true;

    // Check if the improvement is assigned to the current user
    // You may need to adjust these field names based on your improvement data structure
    return [
      improvement.responsible,
      improvement.accountable,
      improvement.consulted,
      improvement.informed,
    ].some((role) =>
      Array.isArray(role) ? role.includes(currentUser) : role === currentUser,
    );
  };

  // Helper function to check if an audit is relevant for the current user in me mode
  const isAuditRelevantForUser = (auditTask) => {
    if (!meModeActive) return true;

    // Check if any linked findings are relevant to the user
    return auditTask.linked_findings.some((findingId) => {
      const finding = findingsData[findingId];
      if (!finding) return false;
      return isFindingRelevantForUser(finding);
    });
  };

  const normalizeDate = (str) => {
    const [y, m, d] = str.split("-");
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  };

  // Filter data based on me mode and date range
  const filteredAuditsTasksArray = Object.values(auditsTasksData || {}).filter(
    (audit) => {
      if (audit.archived) return false;
      const isInDateRange =
        normalizeDate(audit.date) >= startDate &&
        normalizeDate(audit.date) <= endDate;
      return isInDateRange && isAuditRelevantForUser(audit);
    },
  );

  const filteredFindingsArray = Object.values(findingsData || {}).filter(
    (finding) => {
      if (finding.archived) return false;
      const isInDateRange =
        normalizeDate(finding.date) >= startDate &&
        normalizeDate(finding.date) <= endDate;
      return isInDateRange && isFindingRelevantForUser(finding);
    },
  );

  const filteredImprovementsArray = Object.values(
    improvementsData || {},
  ).filter((improvement) => {
    if (improvement.archived) return false;
    const isInDateRange =
      normalizeDate(improvement.date) >= startDate &&
      normalizeDate(improvement.date) <= endDate;
    return isInDateRange && isImprovementRelevantForUser(improvement);
  });

  const { chapters, globalMin, globalMax, chapterCounts } = useMemo(() => {
    const isValidFinding = (f) =>
      f?.risk && f.risk.level && f.risk.level.toLowerCase() !== "none";

    const riskByRiskId = new Map();
    Object.values(risksData || {}).forEach((risk) => {
      const riskId = risk?.risk_id;
      if (!riskId) return;
      riskByRiskId.set(String(riskId).toLowerCase(), risk);
    });

    const getMainChapterKey = (riskId) => {
      if (!riskId) return "Other";
      const normalizedRiskId = String(riskId);
      const lookup = riskByRiskId.get(normalizedRiskId.toLowerCase());
      const qmsId = lookup?.qms_id;
      if (typeof qmsId === "string" && qmsId.length > 0) {
        const key = qmsId.charAt(0).toUpperCase();
        if (key.match(/^[A-Z]$/)) return key;
      }
      if (normalizedRiskId.includes("-")) {
        const key = normalizedRiskId.split("-")[0];
        if (key) return key.toUpperCase();
      }
      const match = normalizedRiskId.match(/^[A-Za-z]+/);
      if (match?.[0]) return match[0].toUpperCase();
      return normalizedRiskId.charAt(0).toUpperCase() || "Other";
    };

    // Use filtered findings array
    const validFindingsArray = filteredFindingsArray.filter(isValidFinding);
    const validFindingsMap = Object.fromEntries(
      validFindingsArray.map((f) => [f.key, f]),
    );

    // Use filtered improvements array
    const filteredImprovementsMap = Object.fromEntries(
      filteredImprovementsArray
        .filter((i) => !i.archived)
        .map((i) => [i.key, i]),
    );

    const groupedData = generateCumulativeTimeSeries(
      validFindingsMap,
      filteredImprovementsMap,
    );

    const chapters = {};
    let globalMin = Infinity;
    let globalMax = -Infinity;

    const auditCountByChapter = {};
    const findingCountByChapter = {};
    const improvementCountByChapter = {};
    const exposureByChapter = {};
    const rpsByChapter = {};

    const auditCountBySubchapter = {};
    const findingCountBySubchapter = {};
    const improvementCountBySubchapter = {};
    const exposureBySubchapter = {};
    const rpsBySubchapter = {};

    Object.keys(groupedData).forEach((subChapter) => {
      const mainChapter = getMainChapterKey(subChapter);

      if (!chapters[mainChapter]) {
        chapters[mainChapter] = {
          subChapters: [],
          cumulativeData: groupedData[subChapter].map((monthData) => ({
            ...monthData,
            count: 0,
          })),
        };
      }

      groupedData[subChapter].forEach((monthData, index) => {
        const count = monthData.count;
        chapters[mainChapter].cumulativeData[index].count += count;
        if (count < globalMin) globalMin = count;
        if (count > globalMax) globalMax = count;
      });

      const matchingRisk = riskByRiskId.get(String(subChapter).toLowerCase());

      const subchapterName = matchingRisk?.description || "";

      const riskLevel = matchingRisk?.level?.toLowerCase() || "none";
      const qmsId =
        typeof matchingRisk?.qms_id === "string" && matchingRisk.qms_id
          ? matchingRisk.qms_id
          : String(subChapter).includes("-")
            ? String(subChapter).split("-")[0]
            : "";

      chapters[mainChapter].subChapters.push({
        key: subChapter,
        name: subchapterName,
        qmsId,
        level: riskLevel,
        data: groupedData[subChapter],
      });
    });

    // Count audits by chapter/subchapter using filtered data
    const auditKeysByChapter = {};
    filteredAuditsTasksArray.forEach((audit) => {
      audit.form?.forEach((formItem) => {
        const riskId = formItem?.risk?.risk_id;
        const qmsId = formItem?.risk?.qms_id;
        const level = formItem?.risk?.level;
        if (level?.toLowerCase() === "none") return;
        const mainChapter =
          typeof qmsId === "string" && qmsId.length > 0
            ? qmsId.charAt(0).toUpperCase()
            : getMainChapterKey(riskId);
        if (riskId) {
          auditCountBySubchapter[riskId] =
            (auditCountBySubchapter[riskId] || 0) + 1;
        }
        if (mainChapter) {
          if (!auditKeysByChapter[mainChapter]) {
            auditKeysByChapter[mainChapter] = new Set();
          }
          auditKeysByChapter[mainChapter].add(audit.key);
        }
      });
    });

    Object.entries(auditKeysByChapter).forEach(([chapter, auditKeys]) => {
      auditCountByChapter[chapter] = auditKeys.size;
    });

    // Calculate RPS using filtered data
    Object.entries(groupedData).forEach(([subChapterKey, monthlyData]) => {
      const mainChapter = getMainChapterKey(subChapterKey);
      const lastMonthCount = monthlyData[monthlyData.length - 1]?.count || 0;

      rpsBySubchapter[subChapterKey] = lastMonthCount;
      rpsByChapter[mainChapter] =
        (rpsByChapter[mainChapter] || 0) + lastMonthCount;
    });

    // Count findings and exposure using filtered data
    validFindingsArray.forEach((finding) => {
      const { risk } = finding;
      const qmsId = risk.qms_id;
      const riskId = risk.risk_id;
      const level = risk.level?.toLowerCase();
      const impact =
        level === "low" ? 1 : level === "medium" ? 2 : level === "high" ? 3 : 0;

      if (qmsId) {
        const chapterKey = String(qmsId).charAt(0).toUpperCase();
        findingCountByChapter[chapterKey] =
          (findingCountByChapter[chapterKey] || 0) + 1;
        exposureByChapter[chapterKey] =
          (exposureByChapter[chapterKey] || 0) + impact;
      }

      if (riskId) {
        findingCountBySubchapter[riskId] =
          (findingCountBySubchapter[riskId] || 0) + 1;
        exposureBySubchapter[riskId] =
          (exposureBySubchapter[riskId] || 0) + impact;
      }
    });

    // Count improvements using filtered data
    filteredImprovementsArray.forEach((improvement) => {
      improvement.linked_findings?.forEach((findingKey) => {
        const finding = validFindingsMap[findingKey];
        if (!isValidFinding(finding)) return;

        const { qms_id, risk_id } = finding.risk;
        if (qms_id) {
          const chapterKey = String(qms_id).charAt(0).toUpperCase();
          improvementCountByChapter[chapterKey] =
            (improvementCountByChapter[chapterKey] || 0) + 1;
        }
        if (risk_id) {
          improvementCountBySubchapter[risk_id] =
            (improvementCountBySubchapter[risk_id] || 0) + 1;
        }
      });
    });

    return {
      chapters,
      globalMin,
      globalMax,
      chapterCounts: {
        auditCountByChapter,
        findingCountByChapter,
        improvementCountByChapter,
        exposureByChapter,
        rpsByChapter,
        auditCountBySubchapter,
        findingCountBySubchapter,
        improvementCountBySubchapter,
        exposureBySubchapter,
        rpsBySubchapter,
      },
    };
  }, [
    filteredFindingsArray,
    filteredAuditsTasksArray,
    filteredImprovementsArray,
    risksData,
  ]);

  const focusRiskKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("risk");
  }, [location.search]);

  useEffect(() => {
    const headerEl = headerRowRef.current;
    const containerEl = containerRef.current;
    if (!headerEl || !containerEl) return;

    const updateHeaderHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      if (!height) return;
      containerEl.style.setProperty(
        "--graph-accordion-header-height",
        `${Math.round(height)}px`,
      );
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeaderHeight);
      return () => window.removeEventListener("resize", updateHeaderHeight);
    }

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!focusRiskKey) return;
    const normalizedRiskKey = focusRiskKey.toLowerCase();
    const matchingRisk = riskByRiskIdLower.get(normalizedRiskKey);
    const qmsId =
      typeof matchingRisk?.qms_id === "string" && matchingRisk.qms_id
        ? matchingRisk.qms_id
        : String(focusRiskKey).includes("-")
          ? String(focusRiskKey).split("-")[0]
          : null;

    const letterKey = qmsId
      ? String(qmsId).charAt(0).toUpperCase()
      : normalizedRiskKey.match(/^[A-Za-z]+/)?.[0]?.toUpperCase() || null;

    if (!letterKey || !chapters[letterKey]) return;

    if (!openLetterGroups.includes(letterKey)) {
      setOpenLetterGroups((prev) =>
        prev.includes(letterKey) ? prev : [...prev, letterKey],
      );
      return;
    }

    if (qmsId && !openChapterGroups.includes(qmsId)) {
      setOpenChapterGroups((prev) =>
        prev.includes(qmsId) ? prev : [...prev, qmsId],
      );
      return;
    }

    if (autoScrollRiskRef.current === normalizedRiskKey) return;
    const target = document.querySelector(
      `[data-risk-key="${normalizedRiskKey}"]`,
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      autoScrollRiskRef.current = normalizedRiskKey;
    }
  }, [
    focusRiskKey,
    chapters,
    openLetterGroups,
    openChapterGroups,
    riskByRiskIdLower,
  ]);

  const toggleLetterAccordion = (letterKey) => {
    if (openLetterGroups.includes(letterKey)) {
      setOpenLetterGroups(openLetterGroups.filter((key) => key !== letterKey));
    } else {
      setOpenLetterGroups([...openLetterGroups, letterKey]);
    }
  };

  const toggleChapterAccordion = (chapterKey) => {
    if (openChapterGroups.includes(chapterKey)) {
      setOpenChapterGroups(
        openChapterGroups.filter((key) => key !== chapterKey),
      );
    } else {
      setOpenChapterGroups([...openChapterGroups, chapterKey]);
    }
  };

  // Function to filter out duplicate improvements based on their key
  const dedupeImprovements = (improvements) => {
    const seenKeys = new Set();
    return improvements.filter((improvement) => {
      if (seenKeys.has(improvement.key)) {
        return false;
      } else {
        seenKeys.add(improvement.key);
        return true;
      }
    });
  };

  const chapterKeys = Object.keys(chapters);

  const visibleChapterKeys = useMemo(() => {
    const hasSearch = normalizedSearchQuery.length > 0;
    const hasLevelFilter = normalizedSelectedRiskLevels.size > 0;

    if (!hasSearch && !hasLevelFilter) return chapterKeys;

    return chapterKeys.filter((chapterKey) => {
      const chapter = chapters[chapterKey];
      const description = chapterDescriptionByKey.get(chapterKey) || "";
      const chapterMatches =
        hasSearch &&
        (String(chapterKey).toLowerCase().includes(normalizedSearchQuery) ||
          description.toLowerCase().includes(normalizedSearchQuery));

      const visibleSubChapters = (chapter?.subChapters || []).filter((sub) => {
        const levelAllowed =
          !hasLevelFilter || normalizedSelectedRiskLevels.has(sub.level);
        if (!levelAllowed) return false;
        if (chapterMatches || !hasSearch) return true;
        const keyMatches = String(sub.key)
          .toLowerCase()
          .includes(normalizedSearchQuery);
        const nameMatches = String(sub.name || "")
          .toLowerCase()
          .includes(normalizedSearchQuery);
        return keyMatches || nameMatches;
      });

      return visibleSubChapters.length > 0;
    });
  }, [
    chapterKeys,
    chapters,
    chapterDescriptionByKey,
    normalizedSearchQuery,
    normalizedSelectedRiskLevels,
  ]);

  // Natural sort function for IDs like A1, A2, B1, etc.
  const naturalSort = (a, b) => {
    const regex = /(\D+)(\d+)/;
    const aMatch = a.match(regex);
    const bMatch = b.match(regex);

    if (aMatch && bMatch) {
      const [, aLetter, aNumber] = aMatch;
      const [, bLetter, bNumber] = bMatch;

      if (aLetter === bLetter) {
        return parseInt(aNumber) - parseInt(bNumber);
      }
      return aLetter.localeCompare(bLetter);
    }

    return a.localeCompare(b);
  };

  const sortedVisibleLetterKeys = useMemo(() => {
    const hasSearch = normalizedSearchQuery.length > 0;
    const hasLevelFilter = normalizedSelectedRiskLevels.size > 0;

    const getVisibleRisksForLetter = (letterKey) => {
      const { subChapters } = chapters[letterKey] || {};
      const letterDescription = chapterDescriptionByKey.get(letterKey) || "";
      const letterMatches =
        hasSearch &&
        (String(letterKey).toLowerCase().includes(normalizedSearchQuery) ||
          letterDescription.toLowerCase().includes(normalizedSearchQuery));

      return (subChapters || []).filter((risk) => {
        const levelAllowed =
          !hasLevelFilter || normalizedSelectedRiskLevels.has(risk.level);
        if (!levelAllowed) return false;
        if (letterMatches || !hasSearch) return true;
        const keyMatches = String(risk.key)
          .toLowerCase()
          .includes(normalizedSearchQuery);
        const nameMatches = String(risk.name || "")
          .toLowerCase()
          .includes(normalizedSearchQuery);
        return keyMatches || nameMatches;
      });
    };

    const letterModels = visibleChapterKeys.map((letterKey) => {
      const visibleRisks = getVisibleRisksForLetter(letterKey);
      const totalExposure = visibleRisks.reduce(
        (sum, risk) =>
          sum + (chapterCounts.exposureBySubchapter?.[risk.key] || 0),
        0,
      );
      const totalPriorityScore = visibleRisks.reduce(
        (sum, risk) => sum + (chapterCounts.rpsBySubchapter?.[risk.key] || 0),
        0,
      );
      return { letterKey, totalExposure, totalPriorityScore };
    });

    return letterModels
      .sort((a, b) => {
        if (sortBy === "priority") {
          const diff = b.totalPriorityScore - a.totalPriorityScore;
          return diff !== 0 ? diff : a.letterKey.localeCompare(b.letterKey);
        }
        if (sortBy === "exposure") {
          const diff = b.totalExposure - a.totalExposure;
          return diff !== 0 ? diff : a.letterKey.localeCompare(b.letterKey);
        }
        return a.letterKey.localeCompare(b.letterKey);
      })
      .map((m) => m.letterKey);
  }, [
    chapters,
    chapterCounts.exposureBySubchapter,
    chapterCounts.rpsBySubchapter,
    chapterDescriptionByKey,
    normalizedSearchQuery,
    normalizedSelectedRiskLevels,
    sortBy,
    visibleChapterKeys,
  ]);

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text?.substring(0, maxLength)}
        <span>...</span>
      </>
    );
  };

  const getImprovementRiskLevelForSubchapter = (
    improvement,
    subChapterKey,
    findingsData,
  ) => {
    const linkedFindings = improvement.linked_findings || [];

    // Find a linked finding that belongs to the current subchapter
    for (const findingKey of linkedFindings) {
      const finding = findingsData?.[findingKey];
      if (finding?.risk?.risk_id === subChapterKey) {
        return finding.risk.level?.toLowerCase() || "none";
      }
    }
  };

  const getLatestStatus = (finding, month) => {
    let latestStatus = finding.status;
    for (const update of finding.updates || []) {
      const updateMonth = update.date?.slice(0, 7);
      if (updateMonth <= month && update.body.includes("Status:")) {
        const match = update.body.match(/Status: (\w+)/);
        if (match) latestStatus = match[1];
      }
    }
    return latestStatus;
  };

  // Collect all linked improvements for a subchapter (chapter-row display), regardless of status
  const collectLinkedImprovementsForSubchapter = (findings, subChapterKey) => {
    const seen = new Set();
    const collected = [];

    findings.forEach((finding) => {
      // Only consider findings for this subchapter
      if (finding?.risk?.risk_id !== subChapterKey) return;

      (finding.linked_improvements || []).forEach((imprKey) => {
        if (seen.has(imprKey)) return;

        const improvement = improvementsData?.[imprKey];
        if (!improvement || improvement.archived) return;

        if (meModeActive && !isImprovementRelevantForUser(improvement)) return;

        seen.add(imprKey);
        collected.push(improvement);
      });
    });

    // Unresolved first (non-Done), then alpha by key for stability
    return collected.sort((a, b) => {
      const statusRank = (impr) => (impr.status === "Done" ? 1 : 0);
      if (statusRank(a) !== statusRank(b)) return statusRank(a) - statusRank(b);
      return (a.key || "").localeCompare(b.key || "");
    });
  };

  return (
    <div className={styles.graphAccordion} ref={containerRef}>
      <div className={styles.accordionHeaderRow} ref={headerRowRef}>
        <div
          className={styles.columnAll}
          onClick={() => {
            const allLetterKeys = visibleChapterKeys;
            const areAllOpen =
              allLetterKeys.length > 0 &&
              allLetterKeys.every((key) => openLetterGroups.includes(key));

            if (areAllOpen) {
              setOpenLetterGroups([]);
            } else {
              setOpenLetterGroups(allLetterKeys);
            }
          }}
        >
          {visibleChapterKeys.length > 0 &&
          visibleChapterKeys.every((key) => openLetterGroups.includes(key)) ? (
            <UpArrow className={styles.toggleAllIcon} />
          ) : (
            <DownArrow className={styles.toggleAllIcon} />
          )}
          <span className={styles.toggleAllText}>{t("graphAccordion.all")}</span>
        </div>
        <div className={styles.columnId}>
          <SortAscIcon className={styles.sortIcon} />
          {t("graphAccordion.id")}
        </div>
        <div className={styles.columnName}>{t("graphAccordion.name")}</div>
        <div className={styles.columnAudits}>{t("graphAccordion.audits")}</div>
        <div className={styles.columnFindings}>{t("graphAccordion.findings")}</div>
        <div className={styles.columnImprovements}>
          {t("graphAccordion.improvements")}
        </div>
        <div className={styles.columnExposure}>
          {t("graphAccordion.exposure")}
          <InfoCircle
            className={styles.infoIcon}
            data-tooltip-id="exposure-tooltip"
          />
        </div>
        <div className={styles.columnRps}>
          <SortAscIcon className={styles.sortIcon} />
          {t("graphAccordion.priorityScore")}
          <InfoCircle
            className={styles.infoIcon}
            data-tooltip-id="rps-tooltip"
          />
        </div>
      </div>
      {Object.keys(chapters).length === 0 ? (
        <div className={styles.noData}>
          {meModeActive
            ? t("graphAccordion.noDataMeMode")
            : t("graphAccordion.noData")}
        </div>
      ) : visibleChapterKeys.length === 0 ? (
        <div className={styles.noData}>{t("graphAccordion.noResults")}</div>
      ) : (
        sortedVisibleLetterKeys.map((letterKey) => {
            const { subChapters } = chapters[letterKey];
            const {
              auditCountBySubchapter,
              findingCountBySubchapter,
              improvementCountBySubchapter,
              exposureBySubchapter,
              rpsBySubchapter,
            } = chapterCounts;

            const letterDescription = chapterDescriptionByKey.get(letterKey) || "";
            const hasSearch = normalizedSearchQuery.length > 0;
            const hasLevelFilter = normalizedSelectedRiskLevels.size > 0;
            const letterMatches =
              hasSearch &&
              (String(letterKey).toLowerCase().includes(normalizedSearchQuery) ||
                letterDescription.toLowerCase().includes(normalizedSearchQuery));

            const visibleRisks = subChapters.filter((risk) => {
              const levelAllowed =
                !hasLevelFilter || normalizedSelectedRiskLevels.has(risk.level);
              if (!levelAllowed) return false;
              if (letterMatches || !hasSearch) return true;
              const keyMatches = String(risk.key)
                .toLowerCase()
                .includes(normalizedSearchQuery);
              const nameMatches = String(risk.name || "")
                .toLowerCase()
                .includes(normalizedSearchQuery);
              return keyMatches || nameMatches;
            });

            const risksByChapter = new Map();
            visibleRisks.forEach((risk) => {
              const qmsId =
                (typeof risk.qmsId === "string" && risk.qmsId) ||
                (String(risk.key).includes("-") ? String(risk.key).split("-")[0] : "");
              if (!qmsId) return;
              if (!risksByChapter.has(qmsId)) risksByChapter.set(qmsId, []);
              risksByChapter.get(qmsId).push(risk);
            });

            const chapterKeys = Array.from(risksByChapter.keys());

            const getChapterRps = (chapterKey) =>
              (risksByChapter.get(chapterKey) || []).reduce(
                (sum, risk) => sum + (rpsBySubchapter[risk.key] || 0),
                0,
              );

            const getChapterExposure = (chapterKey) =>
              (risksByChapter.get(chapterKey) || []).reduce(
                (sum, risk) => sum + (exposureBySubchapter?.[risk.key] || 0),
                0,
              );

            const sortedChapterGroupKeys = [...chapterKeys].sort((a, b) => {
              if (sortBy === "priority") {
                const diff = getChapterRps(b) - getChapterRps(a);
                return diff !== 0 ? diff : naturalSort(a, b);
              }
              if (sortBy === "exposure") {
                const diff = getChapterExposure(b) - getChapterExposure(a);
                return diff !== 0 ? diff : naturalSort(a, b);
              }
              return naturalSort(a, b); // chapter default
            });

            const totalFindings = visibleRisks.reduce(
              (sum, risk) => sum + (findingCountBySubchapter[risk.key] || 0),
              0,
            );

            const isLetterExpanded = openLetterGroups.includes(letterKey);

            return (
              <div key={letterKey} className={styles.letterAccordionItem}>
                <div
                  className={styles.letterAccordionHeader}
                  onClick={() => toggleLetterAccordion(letterKey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleLetterAccordion(letterKey);
                    }
                  }}
                >
                  {isLetterExpanded ? (
                    <UpArrow className={styles.letterToggleIcon} />
                  ) : (
                    <DownArrow className={styles.letterToggleIcon} />
                  )}
                  <div className={styles.letterCircle}>{letterKey}</div>
                  <div className={styles.letterInfo}>
                    <div className={styles.letterTitle}>
                      {t("tableAccordion.chapter")} {letterKey}
                      {letterDescription ? ` — ${letterDescription}` : ""}
                    </div>
                    <div className={styles.letterSubtitle}>
                      {sortedChapterGroupKeys.length}{" "}
                      {t("graphAccordion.subChapters")}
                    </div>
                  </div>
                  <div className={styles.letterCounts}>
                    <span className={styles.letterCountText}>
                      <span className={styles.letterCountNumber}>
                        {visibleRisks.length}
                      </span>{" "}
                      {t("graphAccordion.risks")}
                    </span>
                    <span className={styles.letterFindingsPill}>
                      {totalFindings} {t("graphAccordion.findingsCount")}
                    </span>
                  </div>
                </div>

                {isLetterExpanded && (
                  <div className={styles.letterAccordionBody}>
                    {sortedChapterGroupKeys.map((chapterKey) => {
                      const chapterRisks = risksByChapter.get(chapterKey) || [];
                      const chapterDescription =
                        qmsDescriptionById.get(chapterKey) || "";

                      const totals = {
                        audits: chapterRisks.reduce(
                          (sum, r) => sum + (auditCountBySubchapter[r.key] || 0),
                          0,
                        ),
                        findings: chapterRisks.reduce(
                          (sum, r) =>
                            sum + (findingCountBySubchapter[r.key] || 0),
                          0,
                        ),
                        improvements: chapterRisks.reduce(
                          (sum, r) =>
                            sum + (improvementCountBySubchapter[r.key] || 0),
                          0,
                        ),
                        exposure: chapterRisks.reduce(
                          (sum, r) => sum + (exposureBySubchapter[r.key] || 0),
                          0,
                        ),
                        rps: chapterRisks.reduce(
                          (sum, r) => sum + (rpsBySubchapter[r.key] || 0),
                          0,
                        ),
                      };

                      const cumulativeData = months.map((_, index) => ({
                        count: chapterRisks.reduce(
                          (sum, r) => sum + (r.data?.[index]?.count || 0),
                          0,
                        ),
                      }));

                      const min = globalMin;
                      const max = globalMax;
                      const isChapterExpanded =
                        openChapterGroups.includes(chapterKey);

                      return (
                        <div key={chapterKey} className={styles.accordionItem}>
                          <div
                            className={styles.accordionHeaderWrapper}
                            onClick={() => toggleChapterAccordion(chapterKey)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleChapterAccordion(chapterKey);
                              }
                            }}
                          >
                            <div className={styles.accordionHeader}>
                              <span className={styles.chapterKey}>
                                {chapterKey}
                              </span>
                              <span
                                className={styles.description}
                                title={chapterDescription}
                              >
                                {chapterDescription}
                              </span>

                              <div className={styles.audits}>{totals.audits}</div>
                              <div className={styles.findings}>
                                {totals.findings}
                              </div>
                              <div className={styles.improvements}>
                                {totals.improvements}
                              </div>
                              <div className={styles.exposure}>
                                <span className={styles.exposurePill}>
                                  {totals.exposure}
                                </span>
                              </div>
                              <div className={styles.rps}>
                                <span className={styles.rpsPill}>{totals.rps}</span>
                              </div>
                            </div>

                            <div className={styles.legendRow}>
                              {isChapterExpanded ? (
                                <img
                                  src={UpArrowIcon}
                                  alt="Collapse"
                                  className={styles.icon}
                                />
                              ) : (
                                <img
                                  src={DownArrowIcon}
                                  alt="Expand"
                                  className={styles.icon}
                                />
                              )}
                              <div className={styles.legend}>
                                {cumulativeData.map((item, index) => (
                                  <div
                                    key={index}
                                    className={styles.monthSquare}
                                    data-tooltip-id="month-tooltip"
                                    data-tooltip-html={`${new Date(
                                      months[index] + "-01",
                                    ).toLocaleString("default", {
                                      month: "long",
                                    })} ${t("graphAccordion.priorityScore")}: ${item.count}`}
                                    style={{
                                      backgroundColor: getColor(item.count, min, max),
                                    }}
                                  >
                                    {months[index].slice(5, 7) === "08"
                                      ? "AUG"
                                      : new Date(months[index] + "-01")
                                          .toLocaleString("default", {
                                            month: "short",
                                          })
                                          .toUpperCase()}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {isChapterExpanded && (
                            <div className={styles.accordionBody}>
                              {chapterRisks.map(
                                ({
                                  key: subChapterKey,
                                  name: subChapterName,
                                  level,
                                  data: subChapterData,
                                }) => {
                                  const getColorByLevel = (level) => {
                                    const l = level?.toLowerCase?.();
                                    if (l === "high") return "#F28C94";
                                    if (l === "medium") return "#696969";
                                    if (l === "low") return "#7ADDAF";
                                    return "#696969"; // fallback grey
                                  };

                                  const allFindings = subChapterData
                                    .flatMap((d) => d.relatedFindings || [])
                                    .filter((f) => !!f);

                                  const findingsMap = new Map();

                                  allFindings.forEach((f) => {
                                    if (!f || findingsMap.has(f.key)) return;

                                    const isResolved =
                                      getLatestStatus(
                                        f,
                                        months[months.length - 1],
                                      ) === "Done" &&
                                      (f.linked_improvements || []).every(
                                        (imprKey) =>
                                          improvementsData?.[imprKey]?.status ===
                                          "Done",
                                      );

                                    findingsMap.set(f.key, { ...f, isResolved });
                                  });

                                  const uniqueFindings = Array.from(
                                    findingsMap.values(),
                                  ).sort((a, b) => {
                                    return a.isResolved - b.isResolved; // unresolved first
                                  });

                                  const displayImprovements =
                                    collectLinkedImprovementsForSubchapter(
                                      uniqueFindings,
                                      subChapterKey,
                                    );

                                  return (
                                    <div
                                      key={subChapterKey}
                                      className={styles.graph}
                                      id={`risk-${subChapterKey}`}
                                      data-risk-key={String(subChapterKey).toLowerCase()}
                                    >
                                      <div className={styles.subChapterHeader}>
                                        <div className={styles.subChapterKey}>
                                          <span className={styles.subChapterPill}>
                                            {subChapterKey}
                                          </span>
                                        </div>
                                        <div className={styles.subChapterNameCell}>
                                          <span
                                            className={styles.subChapterName}
                                            title={subChapterName}
                                            data-tooltip-id="subchapter-tooltip"
                                            data-tooltip-content={subChapterName || ""}
                                          >
                                            {subChapterName ?? ""}
                                          </span>
                                          <span
                                            className={classNames(styles.levelPill, {
                                              [styles.low]: level === "low",
                                              [styles.medium]: level === "medium",
                                              [styles.high]: level === "high",
                                            })}
                                          >
                                            {level
                                              ? level.charAt(0).toUpperCase() +
                                                level.slice(1).toLowerCase()
                                              : ""}
                                          </span>
                                        </div>
                                        <span
                                          className={classNames(
                                            styles.count,
                                            styles.auditCount,
                                          )}
                                        >
                                          {auditCountBySubchapter[subChapterKey] || 0}
                                        </span>
                                        <span
                                          className={classNames(
                                            styles.count,
                                            styles.findingCount,
                                          )}
                                        >
                                          {findingCountBySubchapter[subChapterKey] || 0}
                                        </span>
                                        <span
                                          className={classNames(
                                            styles.count,
                                            styles.improvementCount,
                                          )}
                                        >
                                          {improvementCountBySubchapter[subChapterKey] || 0}
                                        </span>
                                        <span
                                          className={classNames(
                                            styles.count,
                                            styles.exposureCount,
                                          )}
                                        >
                                          <span className={styles.exposurePill}>
                                            {exposureBySubchapter[subChapterKey] || 0}
                                          </span>
                                        </span>
                                        <span
                                          className={classNames(
                                            styles.count,
                                            styles.rpsCount,
                                          )}
                                        >
                                          <span className={styles.rpsPill}>
                                            {rpsBySubchapter[subChapterKey] || 0}
                                          </span>
                                        </span>
                                      </div>
                                      <div className={styles.graphWrapper}>
                                        <LineGraph
                                          data={subChapterData.map((item, index) => ({
                                            month: months[index],
                                            count: item.count,
                                            relatedFindings: item.relatedFindings || [],
                                            relatedImprovements: dedupeImprovements(
                                              item.relatedImprovements || [],
                                            ),
                                          }))}
                                          months={months}
                                        />
                                        <div className={styles.currentItems}>
                                          <div className={styles.currentRow}>
                                            <span className={styles.bold}>
                                              {t("graphAccordion.currentFindings")}
                                            </span>
                                            {uniqueFindings.length === 0 ? (
                                              <span style={{ color: "#999" }}>
                                                {t("graphAccordion.none")}
                                              </span>
                                            ) : (
                                              uniqueFindings
                                                .slice(0, 6)
                                                .map((finding, i) => (
                                                  <span key={i}>
                                                    <span
                                                      style={{
                                                        color: getColorByLevel(
                                                          finding?.risk?.level,
                                                        ),
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      <a
                                                        href={`/${orgId}/findings/${finding.key}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                          color: getColorByLevel(
                                                            finding?.risk?.level,
                                                          ),
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {finding.key}:
                                                      </a>
                                                    </span>{" "}
                                                    {truncateText(finding.description, 50)}
                                                    {i <
                                                      Math.min(
                                                        5,
                                                        uniqueFindings.length - 1,
                                                      ) && ", "}
                                                  </span>
                                                ))
                                            )}
                                            {uniqueFindings.length > 0 && (
                                              <Button
                                                text={t(
                                                  "graphAccordion.buttons.seeFindings",
                                                )}
                                                className={styles.linkButton}
                                                icon={<ArrowRight size={16} />}
                                                iconPosition="right"
                                                onClick={() =>
                                                  navigate(
                                                    `/${orgId}/findings?risk=${subChapterKey}`,
                                                  )
                                                }
                                              />
                                            )}
                                          </div>

                                          <div className={styles.currentRow}>
                                            <span className={styles.bold}>
                                              {t("graphAccordion.currentImprovements")}
                                            </span>
                                            {displayImprovements.length === 0 ? (
                                              <span style={{ color: "#999" }}>
                                                {t("graphAccordion.none")}
                                              </span>
                                            ) : (
                                              displayImprovements
                                                .slice(0, 6)
                                                .map((impr, i) => (
                                                  <span key={i}>
                                                    <span
                                                      style={{
                                                        color: getColorByLevel(
                                                          getImprovementRiskLevelForSubchapter(
                                                            impr,
                                                            subChapterKey,
                                                            findingsData,
                                                          ),
                                                        ),
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      <a
                                                        href={`/${orgId}/improvement-projects/${impr.key}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                          color: getColorByLevel(
                                                            getImprovementRiskLevelForSubchapter(
                                                              impr,
                                                              subChapterKey,
                                                              findingsData,
                                                            ),
                                                          ),
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {impr.key}:
                                                      </a>
                                                    </span>{" "}
                                                    {truncateText(impr.description, 50)}
                                                    {i <
                                                      Math.min(
                                                        5,
                                                        displayImprovements.length - 1,
                                                      ) && ", "}
                                                  </span>
                                                ))
                                            )}
                                            {displayImprovements.length > 0 && (
                                              <Button
                                                text={t(
                                                  "graphAccordion.buttons.seeImprovements",
                                                )}
                                                className={styles.linkButton}
                                                icon={<ArrowRight size={16} />}
                                                iconPosition="right"
                                                onClick={() =>
                                                  navigate(
                                                    `/${orgId}/improvement-projects?risk=${subChapterKey}`,
                                                  )
                                                }
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
      )}
      <Tooltip id="month-tooltip" place="top" className={styles.tooltip} />
      <Tooltip id="subchapter-tooltip" place="top" className={styles.tooltip} />
      <Tooltip
        id="exposure-tooltip"
        place="top"
        content={t("graphAccordion.tooltip.exposure")}
        className={classNames(styles.tooltip, styles.headerTooltip)}
      />
      <Tooltip
        id="rps-tooltip"
        place="top"
        content={t("graphAccordion.tooltip.rps")}
        className={classNames(styles.tooltip, styles.headerTooltip)}
      />
    </div>
  );
};

export default GraphAccordion;
